/**
 * The code on a bill, and the Code 128 (subset B) symbol that carries it —
 * ported from the mobile app (Mobile/src/lib/bill.js, lib/barcode.js,
 * components/Barcode.js) so a bill looks and scans the same everywhere.
 *
 * Shape: LUM-<order id>. Verifying a bill means fetching that order from the
 * server: the barcode is a fast, accurate way of typing a number, not a
 * credential. Nothing is trusted because it scanned.
 */
import React from 'react';

const PREFIX = 'LUM-';

/** The code to print on an order's bill, or null if it has no id. */
export const billCodeFor = order => {
  const id = order && (order.id ?? order.order_id);
  return id == null ? null : `${PREFIX}${id}`;
};

/** Read an order id back out of a scanned or typed code; bare numbers too. */
export const orderIdFromCode = raw => {
  if (typeof raw !== 'string') return null;
  const text = raw.trim().toUpperCase();
  if (!text) return null;
  const body = text.startsWith(PREFIX) ? text.slice(PREFIX.length) : text;
  return /^\d+$/.test(body) ? body : null;
};

/* The 107 element patterns, index 0–106. Each digit is the width of one bar
   or space, alternating bar first. */
const PATTERNS = [
  '212222','222122','222221','121223','121322','131222','122213','122312','132212','221213',
  '221312','231212','112232','122132','122231','113222','123122','123221','223211','221132',
  '221231','213212','223112','312131','311222','321122','321221','312212','322112','322211',
  '212123','212321','232121','111323','131123','131321','112313','132113','132311','211313',
  '231113','231311','112133','112331','132131','113123','113321','133121','313121','211331',
  '231131','213113','213311','213131','311123','311321','331121','312113','312311','332111',
  '314111','221411','431111','111224','111422','121124','121421','141122','141221','112214',
  '112412','122114','122411','142112','142211','241211','221114','413111','241112','134111',
  '111242','121142','121241','114212','124112','124211','411212','421112','421211','212141',
  '214121','412121','111143','111341','131141','114113','114311','411113','411311','113141',
  '114131','311141','411131','211412','211214','211232','2331112',
];
const START_B = 104;
const STOP = 106;

const canEncode = text =>
  typeof text === 'string' && text.length > 0 &&
  [...text].every(c => c.charCodeAt(0) >= 32 && c.charCodeAt(0) <= 126);

/** Bar/space widths, alternating, starting with a bar. */
export const encode128 = text => {
  if (!canEncode(text)) throw new Error('Code 128 B cannot encode that text');
  const values = [...text].map(ch => ch.charCodeAt(0) - 32);
  const checksum = (START_B + values.reduce((sum, v, i) => sum + v * (i + 1), 0)) % 103;
  const widths = [];
  [START_B, ...values, checksum, STOP].forEach(e => [...PATTERNS[e]].forEach(d => widths.push(Number(d))));
  return widths;
};

/**
 * The symbol, drawn as SVG. Always dark bars — a barcode is read by a
 * machine, and light-on-dark (what a dark theme's ink gives) is an inverted
 * symbol most scanners cannot read. The caller puts it on a white plate,
 * whose margin is also the quiet zone Code 128 needs either side.
 */
export const Barcode = ({ value, width = 260, height = 64, color = '#111111' }) => {
  if (!canEncode(value)) return null;
  const bars = encode128(value);
  const modules = bars.reduce((a, b) => a + b, 0);
  const unit = width / modules;
  let x = 0;
  const rects = [];
  bars.forEach((w, i) => { if (i % 2 === 0) rects.push({ x, w: w * unit }); x += w * unit; });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={value} style={{ display: 'block' }} shapeRendering="crispEdges">
      {rects.map((r, i) => <rect key={i} x={r.x} y={0} width={r.w} height={height} fill={color} />)}
    </svg>
  );
};
